import React, { useMemo, useState } from 'react';
import { Wand2, Loader2, CheckCircle2, XCircle, AlertTriangle, Sparkles } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import ImageUploadField from './ImageUploadField';
import RefListInput from './RefListInput';
import { renderMarkdown } from '../services/markdown';
import FaqEditor from './FaqEditor';

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const slugify = (s) => norm(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const seoChecks = (form, similar, slugPrefix = '/marque/') => {
  const kw = norm(form.main_keyword).trim();
  const words = (form.description || '').trim().split(/\s+/).filter(Boolean).length;
  const occurrences = kw ? (norm(form.description).match(new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length : 0;
  const density = words ? (occurrences * kw.split(' ').length / words) * 100 : 0;
  const metaLen = (form.meta_description || '').length;
  const top = similar?.[0];
  return [
    { id: 'kw', ok: !!kw, label: kw ? `Mot-clé principal défini : « ${form.main_keyword} »` : 'Définir le mot-clé principal ([Produit] [MARQUE])' },
    { id: 'h1', ok: kw && norm(form.h1).includes(kw), label: 'Mot-clé présent dans le H1' },
    { id: 'title', ok: kw && norm(form.seo_title).includes(kw) && norm(form.seo_title).includes('otopiece'), label: 'Mot-clé + OTOPIECES dans le titre SEO' },
    { id: 'meta', ok: kw && norm(form.meta_description).includes(kw) && metaLen >= 120 && metaLen <= 160, warn: kw && norm(form.meta_description).includes(kw) && metaLen > 0, label: `Méta-description 120–160 caractères avec le mot-clé (${metaLen})` },
    { id: 'slug', ok: kw && slugify(form.slug) === slugify(form.main_keyword), warn: !!form.slug, label: form.slug ? `Slug = mot-clé (${slugPrefix}${slugify(form.slug)})` : 'Slug manquant ([produit]-[marque])' },
    { id: 'words', ok: words >= 250 && words <= 400, warn: words >= 180, label: `Description longue 250–400 mots (${words})` },
    { id: 'density', ok: occurrences >= 2 && density <= 3, warn: occurrences >= 1, label: `Mot-clé utilisé naturellement (${occurrences} occurrence${occurrences > 1 ? 's' : ''}, densité ${density.toFixed(1)} %)` },
    { id: 'excerpt', ok: (form.excerpt || '').length >= 60, label: 'Extrait court (1–2 phrases commerciales)' },
    { id: 'tags', ok: (form.tags || []).length >= 5, warn: (form.tags || []).length > 0, label: `Tags (${(form.tags || []).length}, 5 minimum conseillés)` },
    { id: 'faq', ok: (form.faq || []).filter(f => f.question && f.answer).length >= 3, warn: (form.faq || []).length > 0, label: `FAQ 3–5 questions/réponses (${(form.faq || []).filter(f => f.question && f.answer).length})` },
    { id: 'unique', ok: !top || top.similarity < 35, warn: top && top.similarity < 55, label: top ? `Texte différent des autres marques (max ${top.similarity} % commun avec ${top.name})` : 'Unicité du texte (vérifiée après enregistrement)' },
  ];
};

const Field = ({ label, hint, children, count, max }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      {max !== undefined && <span className={`text-[11px] ${count > max ? 'text-red-600' : 'text-gray-400'}`}>{count}/{max}</span>}
    </div>
    {children}
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const BrandSeoForm = ({ form, setForm, brandName, onGenerate, generating, similar, slugPrefix = '/marque/', productHint = 'Pneus', onFill = null, filling = false, langs = 0 }) => {
  const [preview, setPreview] = useState(false);
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const checks = useMemo(() => seoChecks(form, similar, slugPrefix), [form, similar, slugPrefix]);
  const score = checks.filter(c => c.ok).length;
  const words = (form.description || '').trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <ImageUploadField testId="brand-logo" label="Logo (PNG transparent conseillé)" value={form.logo} onChange={(url) => setForm(f => ({ ...f, logo: url }))} />

      <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">Générer un texte type (format SEO OTOPIECES)</p>
          <p className="text-xs text-gray-600">Pré-remplit mot-clé, H1, titre SEO, slug, méta, description (~250 mots), extrait et tags à partir des catégories réelles de la marque. À relire et personnaliser.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="brand-main-product-input" value={form.main_product} onChange={set('main_product')} placeholder="Produit principal (ex : Pneus)" className="w-48 bg-white" />
          <Button type="button" data-testid="brand-generate-button" onClick={() => onGenerate(form.main_product)} disabled={generating} variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-100">
            {generating ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Wand2 size={16} className="mr-1" />} Générer
          </Button>
          {onFill && (
            <Button type="button" data-testid="brand-fill-seo-button" onClick={onFill} disabled={filling} className="bg-orange-500 hover:bg-orange-600" title="Rédige le SEO FR s'il est vide puis le traduit en EN / DE / ES / IT et enregistre (arrière-plan)">
              {filling ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Sparkles size={16} className="mr-1" />} {filling ? 'Remplissage IA…' : `Remplir par IA (5 langues) · ${langs}/5`}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Mot-clé principal" hint="[Produit principal] [MARQUE] — ex : Pneus MICHELIN">
          <Input data-testid="brand-main-keyword-input" value={form.main_keyword} onChange={set('main_keyword')} placeholder={`Pneus ${brandName}`} />
        </Field>
        <Field label="Slug / URL" hint={`${slugPrefix}${slugify(form.slug) || slugify(form.main_keyword) || brandName.toLowerCase()}`}>
          <Input data-testid="brand-slug-input" value={form.slug} onChange={set('slug')} placeholder={slugify(form.main_keyword) || `pneus-${brandName.toLowerCase()}`} />
        </Field>
        <Field label="H1 (titre de la page)" hint="[Produit principal] [MARQUE] : [avantage principal]" count={form.h1.length} max={120}>
          <Input data-testid="brand-h1-input" value={form.h1} onChange={set('h1')} placeholder={`Pneus ${brandName} : qualité, sécurité et performance`} />
        </Field>
        <Field label="Titre SEO (onglet / Google)" hint="[Produit principal] [MARQUE] au meilleur prix | OTOPIECES" count={form.seo_title.length} max={70}>
          <Input data-testid="brand-seo-title-input" value={form.seo_title} onChange={set('seo_title')} placeholder={`Pneus ${brandName} au meilleur prix | OTOPIECES`} />
        </Field>
        <Field label="Méta-description" hint="150–160 caractères, mot-clé principal + OTOPIECES" count={form.meta_description.length} max={160}>
          <Textarea data-testid="brand-meta-description-input" rows={3} value={form.meta_description} onChange={set('meta_description')} />
        </Field>
        <Field label="Extrait court" hint="1–2 phrases commerciales, affichées sous le titre de la page" count={form.excerpt.length} max={400}>
          <Textarea data-testid="brand-excerpt-input" rows={3} value={form.excerpt} onChange={set('excerpt')} />
        </Field>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-600">Description longue SEO <span className="text-gray-400">(Markdown · {words} mots · cible 250–400)</span></label>
          <button type="button" data-testid="brand-description-preview-toggle" onClick={() => setPreview(v => !v)} className={`text-xs px-2 py-1 rounded border ${preview ? 'bg-orange-500 text-white border-orange-500' : 'text-gray-600 border-gray-300 hover:border-orange-400'}`}>
            {preview ? 'Éditer' : 'Aperçu'}
          </button>
        </div>
        {preview ? (
          <div data-testid="brand-description-preview" className="prose-legal border border-gray-200 rounded-md p-4 min-h-[200px] text-sm text-gray-700 bg-gray-50" dangerouslySetInnerHTML={{ __html: renderMarkdown(form.description) }} />
        ) : (
          <Textarea data-testid="brand-description-input" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value.slice(0, 8000) }))} rows={12} className="text-sm" placeholder="Introduction de la marque, expertise, gammes, avantages, conseil, paragraphe final orienté OTOPIECES…" />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RefListInput testId="brand-tags" label="Tags" hint="Séparés par des virgules ou Entrée. Affichés en « Recherches associées » + meta keywords." placeholder="pneus Michelin, pneu Michelin pas cher…" values={form.tags} onChange={(tags) => setForm(f => ({ ...f, tags }))} />
        <RefListInput testId="brand-secondary-keywords" label="Mots-clés secondaires" hint="[produit] [marque] pas cher, au meilleur prix, acheter…" placeholder="pneus Michelin pas cher" values={form.secondary_keywords} onChange={(secondary_keywords) => setForm(f => ({ ...f, secondary_keywords }))} />
      </div>

      <FaqEditor testId="brand-faq" items={form.faq || []} onChange={(faq) => setForm(f => ({ ...f, faq }))} />

      <div className="rounded-lg border border-gray-200 p-4" data-testid="brand-seo-checklist">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-900">Optimisation SEO</p>
          <span data-testid="brand-seo-score" className={`text-xs font-bold px-2 py-0.5 rounded-full ${score >= 9 ? 'bg-green-100 text-green-700' : score >= 5 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{score}/{checks.length}</span>
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {checks.map(c => (
            <li key={c.id} data-testid={`brand-seo-check-${c.id}`} className="flex items-start gap-2 text-xs text-gray-700">
              {c.ok ? <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> : c.warn ? <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" /> : <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />}
              <span>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default BrandSeoForm;
